import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { InputNumber } from 'primereact/inputnumber';
import { Toast } from 'primereact/toast';
import { Editor } from 'primereact/editor';
import { VoucherWithCodes, CreateVoucherRequest, UpdateVoucherRequest } from '../../../../../types/voucher.types';
import './VoucherCodeRandomForm.css';

interface VoucherCodeRandomFormProps {
    visible: boolean;
    onHide: () => void;
    voucher?: VoucherWithCodes | null;
    onSubmit: (data: CreateVoucherRequest | UpdateVoucherRequest) => Promise<void>;
    loading?: boolean;
    isEdit?: boolean;
}

const VoucherCodeRandomForm: React.FC<VoucherCodeRandomFormProps> = ({
    visible,
    onHide,
    voucher,
    onSubmit,
    loading = false,
    isEdit = false
}) => {
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState<any>({
        title: '',
        description: '',
        code_prefix: '',
        number_of_codes: 1,
        discount_type: 'percentage',
        discount_value: 0,
        min_order_amount: null,
        max_discount: null,
        valid_from: new Date(),
        valid_to: null,
        total_redemption_limit: 1,
        voucher_type_vehicle: 'both',
        is_active: true,
        user_redemption_limit: null,
        multi_code: true,
        is_public: false
    });
    const toast = useRef<Toast>(null);

    const discountTypeOptions = [
        { label: 'Phần trăm (%)', value: 'percentage' },
        { label: 'Số tiền cố định (VND)', value: 'fixed_amount' }
    ];

    const vehicleTypeOptions = [
        { label: 'Tất cả phương tiện', value: 'both' },
        { label: 'Ô tô', value: 'car' },
        { label: 'Xe máy', value: 'motorbike' }
    ];

    // Initialize form data when voucher changes
    useEffect(() => {
        if (voucher && isEdit) {
            console.log('Loading voucher data for edit:', voucher);
            setFormData({
                title: voucher.title || '',
                description: voucher.description || '',
                code_prefix: voucher.code_prefix || '',
                number_of_codes: voucher.number_of_codes || 1,
                discount_type: voucher.discount_value && voucher.discount_value > 0 ? 'fixed_amount' : 'percentage',
                discount_value: voucher.discount_value || voucher.discount_percentage || 0,
                min_order_amount: voucher.minimum_amount,
                max_discount: voucher.max_discount,
                valid_from: voucher.start_date ? new Date(voucher.start_date) : new Date(),
                valid_to: voucher.end_date ? new Date(voucher.end_date) : null,
                total_redemption_limit: voucher.total_redemption_limit || 1,
                voucher_type_vehicle: voucher.voucher_type_vehicle || 'both',
                is_active: voucher.is_active ?? true,
                user_redemption_limit: voucher.user_redemption_limit,
                multi_code: true,
                is_public: false
            });
        } else if (!isEdit) {
            // Reset form for new voucher
            console.log('Resetting form for new voucher');
            setFormData({
                title: '',
                description: '',
                code_prefix: '',
                number_of_codes: 1,
                discount_type: 'percentage',
                discount_value: 0,
                min_order_amount: null,
                max_discount: null,
                valid_from: new Date(),
                valid_to: null,
                total_redemption_limit: 1,
                voucher_type_vehicle: 'both',
                is_active: true,
                user_redemption_limit: null,
                multi_code: true,
                is_public: false
            });
        }
    }, [voucher, isEdit]); // Removed 'visible' from dependencies

    const handleInputChange = (field: string, value: any) => {
        setFormData((prev: any) => ({
            ...prev,
            [field]: value
        }));
    };

    const validateForm = (): string[] => {
        const errors: string[] = [];

        if (!formData.title?.trim()) {
            errors.push('Tiêu đề voucher là bắt buộc');
        } else if (formData.title.length < 3) {
            errors.push('Tiêu đề phải có ít nhất 3 ký tự');
        } else if (formData.title.length > 100) {
            errors.push('Tiêu đề không được quá 100 ký tự');
        }

        if (!formData.description?.trim()) {
            errors.push('Mô tả voucher là bắt buộc');
        } else if (formData.description.length < 10) {
            errors.push('Mô tả phải có ít nhất 10 ký tự');
        } else if (formData.description.length > 1000) {
            errors.push('Mô tả không được quá 1000 ký tự');
        }

        if (!formData.code_prefix?.trim()) {
            errors.push('Tiền tố mã code là bắt buộc');
        }

        if (!formData.number_of_codes || formData.number_of_codes < 1) {
            errors.push('Số lượng mã code phải lớn hơn 0');
        }

        if (formData.number_of_codes > 1000) {
            errors.push('Số lượng mã code không được vượt quá 1000');
        }

        if (!formData.discount_value || formData.discount_value <= 0) {
            errors.push('Giá trị giảm giá phải lớn hơn 0');
        }

        if (formData.discount_type === 'percentage' && formData.discount_value > 100) {
            errors.push('Giá trị giảm giá phần trăm không được vượt quá 100%');
        }

        // Validate max_discount when using percentage discount
        if (formData.discount_type === 'percentage') {
            if (!formData.max_discount || formData.max_discount <= 0) {
                errors.push('Số tiền giảm tối đa là bắt buộc khi chọn giảm theo phần trăm');
            }
        }

        if (!formData.total_redemption_limit || formData.total_redemption_limit < 1) {
            errors.push('Tổng số lần sử dụng phải lớn hơn 0');
        }

        if (!formData.valid_from) {
            errors.push('Ngày bắt đầu hiệu lực là bắt buộc');
        }

        if (formData.valid_to && formData.valid_from && formData.valid_to <= formData.valid_from) {
            errors.push('Ngày kết thúc phải sau ngày bắt đầu');
        }

        return errors;
    };

    const handleSubmit = async () => {
        const errors = validateForm();
        if (errors.length > 0) {
            toast.current?.show({
                severity: 'error',
                summary: 'Lỗi validation',
                detail: errors.join(', '),
                life: 5000
            });
            return;
        } setSubmitting(true);
        try {
            const submitData = {
                voucher_type: 'random_code',
                title: formData.title,
                description: formData.description,
                start_date: formData.valid_from?.toISOString(),
                end_date: formData.valid_to?.toISOString() || null,
                // Map discount fields correctly based on type
                discount_value: formData.discount_type === 'fixed_amount' ? formData.discount_value : null,
                discount_percentage: formData.discount_type === 'percentage' ? formData.discount_value : null,
                max_discount: formData.max_discount,
                minimum_amount: formData.min_order_amount,
                max_usage: 1, // For random code vouchers, each code can be used once
                voucher_type_vehicle: formData.voucher_type_vehicle,
                total_redemption_limit: formData.total_redemption_limit,
                user_redemption_limit: formData.user_redemption_limit,
                // Random code specific fields
                code_prefix: formData.code_prefix,
                number_of_codes: formData.number_of_codes,
                multi_code: true,
                is_public: false
            };

            if (isEdit && voucher) {
                await onSubmit({ id: voucher.id, ...submitData } as UpdateVoucherRequest);
            } else {
                await onSubmit(submitData as CreateVoucherRequest);
            }

            onHide();
        } catch (error) {
            console.error('Error submitting voucher:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Lỗi',
                detail: 'Có lỗi xảy ra khi lưu voucher',
                life: 3000
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <Toast ref={toast} />
            <Dialog
                visible={visible}
                style={{ width: '50vw', minWidth: '500px' }}
                header={isEdit ? 'Chỉnh sửa Voucher Random Code' : 'Tạo Voucher Random Code mới'}
                modal
                className="p-fluid voucher-random-form-dialog"
                onHide={onHide}
                footer={
                    <div>
                        <Button
                            label="Hủy"
                            icon="pi pi-times"
                            text
                            onClick={onHide}
                            disabled={loading || submitting}
                        />
                        <Button
                            label={isEdit ? 'Cập nhật' : 'Tạo mới'}
                            icon="pi pi-check"
                            onClick={handleSubmit}
                            loading={loading || submitting}
                        />
                    </div>
                }
            >
                <div className="formgrid grid">
                    {/* Title */}
                    <div className="field col-12">
                        <label htmlFor="title" className="block text-900 font-medium mb-2">
                            Tiêu đề voucher <span className="required">*</span>
                        </label>
                        <InputText
                            id="title"
                            value={formData.title}
                            onChange={(e) => handleInputChange('title', e.target.value)}
                            placeholder="Nhập tiêu đề voucher"
                            className={!formData.title?.trim() ? 'p-invalid' : ''}
                        />
                    </div>

                    {/* Description */}
                    <div className="field col-12">
                        <label htmlFor="description" className="block text-900 font-medium mb-2">
                            Mô tả chi tiết <span className="required">*</span>
                        </label>
                        <Editor
                            value={formData.description}
                            onTextChange={(e) => handleInputChange('description', e.htmlValue || '')}
                            placeholder="Nhập mô tả chi tiết về voucher..."
                            className={!formData.description?.trim() ? 'p-invalid' : ''}
                            style={{ height: '400px' }}

                        />
                        <small className="text-600">
                            Mô tả chi tiết về voucher, điều kiện sử dụng, v.v. (10-1000 ký tự)
                        </small>
                    </div>

                    {/* Code Prefix and Number of Codes */}
                    <div className="field col-6">
                        <label htmlFor="code_prefix" className="block text-900 font-medium mb-2">
                            Tiền tố mã code <span className="required">*</span>
                        </label>
                        <InputText
                            id="code_prefix"
                            value={formData.code_prefix}
                            onChange={(e) => handleInputChange('code_prefix', e.target.value.toUpperCase())}
                            placeholder="VD: RIDE"
                            className={!formData.code_prefix?.trim() ? 'p-invalid' : ''}
                            disabled={isEdit}
                        />
                        {isEdit && (
                            <small className="text-yellow-600">
                                Không thể thay đổi tiền tố khi chỉnh sửa
                            </small>
                        )}
                    </div>

                    <div className="field col-6">
                        <label htmlFor="number_of_codes" className="block text-900 font-medium mb-2">
                            Số lượng mã code <span className="required">*</span>
                        </label>
                        <InputNumber
                            id="number_of_codes"
                            value={formData.number_of_codes}
                            onValueChange={(e) => handleInputChange('number_of_codes', e.value)}
                            placeholder="Nhập số lượng mã"
                            min={1}
                            max={1000}
                            className={!formData.number_of_codes || formData.number_of_codes < 1 ? 'p-invalid' : ''}
                            disabled={isEdit}
                        />
                        {isEdit && (
                            <small className="text-yellow-600">
                                Không thể thay đổi số lượng mã khi chỉnh sửa
                            </small>
                        )}
                    </div>

                    {/* Discount Type and Value */}
                    <div className="field col-6">
                        <label htmlFor="discount_type" className="block text-900 font-medium mb-2">
                            Loại giảm giá <span className="required">*</span>
                        </label>
                        <Dropdown
                            id="discount_type"
                            value={formData.discount_type}
                            onChange={(e) => handleInputChange('discount_type', e.value)}
                            options={discountTypeOptions}
                            placeholder="Chọn loại giảm giá"
                        />
                    </div>

                    <div className="field col-6">
                        <label htmlFor="discount_value" className="block text-900 font-medium mb-2">
                            Giá trị giảm giá <span className="required">*</span>
                        </label>
                        <InputNumber
                            id="discount_value"
                            value={formData.discount_value}
                            onChange={(e) => handleInputChange('discount_value', e.value)}
                            placeholder={formData.discount_type === 'percentage' ? 'VD: 10' : 'VD: 50000'}
                            suffix={formData.discount_type === 'percentage' ? '%' : ' VND'}
                            min={0}
                            max={formData.discount_type === 'percentage' ? 100 : undefined}
                            className={!formData.discount_value || formData.discount_value <= 0 ? 'p-invalid' : ''}
                        />
                    </div>

                    {/* Min Order Amount and Max Discount Amount */}
                    <div className="field col-6">
                        <label htmlFor="min_order_amount" className="block text-900 font-medium mb-2">
                            Giá trị đơn hàng tối thiểu
                        </label>
                        <InputNumber
                            id="min_order_amount"
                            value={formData.min_order_amount}
                            onChange={(e) => handleInputChange('min_order_amount', e.value)}
                            placeholder="Không giới hạn"
                            suffix=" VND"
                            min={0}
                        />
                    </div>

                    <div className="field col-6">
                        <label htmlFor="max_discount" className="block text-900 font-medium mb-2">
                            Số tiền giảm tối đa
                        </label>
                        <InputNumber
                            id="max_discount"
                            value={formData.max_discount}
                            onChange={(e) => handleInputChange('max_discount', e.value)}
                            placeholder="Không giới hạn"
                            suffix=" VND"
                            min={0}
                            disabled={formData.discount_type === 'fixed_amount'}
                        />
                        {formData.discount_type === 'fixed_amount' && (
                            <small className="text-gray-500">
                                Tự động bằng giá trị giảm giá cho loại số tiền cố định
                            </small>
                        )}
                    </div>

                    {/* Valid Dates */}
                    <div className="field col-6">
                        <label htmlFor="valid_from" className="block text-900 font-medium mb-2">
                            Ngày bắt đầu <span className="required">*</span>
                        </label>
                        <Calendar
                            id="valid_from"
                            value={formData.valid_from}
                            onChange={(e) => handleInputChange('valid_from', e.value)}
                            showTime
                            hourFormat="24"
                            placeholder="Chọn ngày bắt đầu"
                            className={!formData.valid_from ? 'p-invalid' : ''}
                        />
                    </div>

                    <div className="field col-6">
                        <label htmlFor="valid_to" className="block text-900 font-medium mb-2">
                            Ngày kết thúc
                        </label>
                        <Calendar
                            id="valid_to"
                            value={formData.valid_to}
                            onChange={(e) => handleInputChange('valid_to', e.value)}
                            showTime
                            hourFormat="24"
                            placeholder="Không giới hạn"
                            minDate={formData.valid_from}
                        />
                    </div>

                    {/* Redemption Limits */}
                    <div className="field col-6">
                        <label htmlFor="total_redemption_limit" className="block text-900 font-medium mb-2">
                            Tổng số lần sử dụng <span className="required">*</span>
                        </label>
                        <InputNumber
                            id="total_redemption_limit"
                            value={formData.total_redemption_limit}
                            onValueChange={(e) => handleInputChange('total_redemption_limit', e.value)}
                            placeholder="Nhập số lần sử dụng"
                            min={1}
                            className={!formData.total_redemption_limit || formData.total_redemption_limit < 1 ? 'p-invalid' : ''}
                        />
                        <small className="text-gray-600">
                            Tổng số lần voucher có thể được sử dụng trên toàn hệ thống
                        </small>
                    </div>

                    <div className="field col-6">
                        <label htmlFor="user_redemption_limit" className="block text-900 font-medium mb-2">
                            Giới hạn sử dụng mỗi user
                        </label>
                        <InputNumber
                            id="user_redemption_limit"
                            value={formData.user_redemption_limit}
                            onValueChange={(e) => handleInputChange('user_redemption_limit', e.value)}
                            placeholder="Không giới hạn"
                            min={1}
                        />
                        <small className="text-gray-600">
                            Số lần tối đa mỗi user có thể sử dụng voucher này
                        </small>
                    </div>

                    {/* Vehicle Types */}
                    <div className="field col-6">
                        <label htmlFor="voucher_type_vehicle" className="block text-900 font-medium mb-2">
                            Loại phương tiện <span className="required">*</span>
                        </label>
                        <Dropdown
                            id="voucher_type_vehicle"
                            value={formData.voucher_type_vehicle}
                            onChange={(e) => handleInputChange('voucher_type_vehicle', e.value)}
                            options={vehicleTypeOptions}
                            placeholder="Chọn loại phương tiện"
                        />
                    </div>

                    {/* Business Rule Info */}
                    <div className="field col-12">
                        <div className="p-3 bg-blue-50 border-round">
                            <h6 className="text-blue-900 mt-0 mb-2">💡 Lưu ý về Voucher Random Code:</h6>
                            <ul className="text-blue-800 text-sm ml-3 mb-0">
                                <li>Voucher này sẽ tự động tạo ra nhiều mã code ngẫu nhiên</li>
                                <li>Mỗi mã code chỉ có thể sử dụng một lần</li>
                                <li>Voucher này luôn là loại riêng tư (không công khai)</li>
                                <li>Tổng số lần sử dụng là bắt buộc và phải ≥ số lượng mã code</li>
                                <li>Tiền tố và số lượng mã không thể thay đổi sau khi tạo</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </Dialog>
        </>
    );
};

export default VoucherCodeRandomForm;
